import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { cn } from "../lib/utils";
import { usePermissions } from "../hooks/usePermissions";
import {
  ChevronDown,
  LayoutDashboard,
  Users,
  Shield,
  ShieldCheck,
  UserCheck,
  User,
  Wallet,
  ArrowDownToLine,
  ArrowUpFromLine,
  BarChart2,
  Radio,
  Globe,
  Ticket,
  Headphones,
  Bell,
  Bot,
  Layers,
  Coins,
  Gem,
  Settings,
  ArrowLeftRight,
  FileText,
  ScrollText,
  Landmark,
  SlidersHorizontal,
  CircleHelp,
  Newspaper,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
} from "./ui/sidebar";

/* ── Primitives ─────────────────────────────────────────── */

export function NavItem({ to, icon: Icon, children, exact = false }) {
  const location = useLocation();
  const isActive = exact
    ? location.pathname === to
    : location.pathname === to || location.pathname.startsWith(to + "/");

  return (
    <Link
      to={to}
      className={cn(
        "relative flex items-center gap-3 rounded-md py-1.5 pl-3 pr-3 text-sm transition-colors",
        isActive
          ? "bg-[#1a1d23] text-foreground before:absolute before:left-0 before:top-1/2 before:h-5 before:w-px before:-translate-y-1/2 before:bg-ginox-green"
          : "text-muted-foreground hover:bg-white/[0.04] hover:text-foreground"
      )}
    >
      <Icon className="h-4 w-4 shrink-0" />
      {children}
    </Link>
  );
}

function SubItem({ to, children, exact = false }) {
  const location = useLocation();
  const isActive = exact
    ? location.pathname === to
    : location.pathname === to || location.pathname.startsWith(to + "/");

  return (
    <Link
      to={to}
      className={cn(
        "flex items-center rounded-md px-2.5 py-1.5 text-sm transition-colors",
        isActive
          ? "text-foreground bg-white/[0.06]"
          : "text-muted-foreground hover:text-foreground hover:bg-white/[0.04]"
      )}
    >
      {children}
    </Link>
  );
}

function NavGroup({ icon: Icon, label, children, matchPaths = [] }) {
  const location = useLocation();
  const isAnyActive = matchPaths.some(
    (p) => location.pathname === p || location.pathname.startsWith(p + "/")
  );
  const [open, setOpen] = useState(isAnyActive);

  useEffect(() => {
    if (isAnyActive) setOpen(true);
  }, [isAnyActive]);

  return (
    <div>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 rounded-md px-3 py-2 text-sm text-muted-foreground hover:bg-white/[0.04] hover:text-foreground transition-colors"
      >
        <Icon className="h-4 w-4 shrink-0" />
        <span className="flex-1 text-left">{label}</span>
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 transition-transform duration-200",
            open && "rotate-180"
          )}
        />
      </button>
      {open && (
        <div className="ml-4 mt-0.5 border-l border-border/40 pl-3 space-y-0.5">
          {children}
        </div>
      )}
    </div>
  );
}

/* ── Nav Content ─────────────────────────────────────────── */

function NavContent() {
  const { can } = usePermissions();

  const hasReports =
    can("GET:/api/admin/reports/income/:income_type") ||
    can("GET:/api/admin/reports/sponsor-bonus-report") ||
    can("GET:/api/admin/reports/statement/:wallet_type") ||
    can("GET:/api/admin/reports/buy-history") ||
    can("GET:/api/admin/reports/bfm-buy-history") ||
    can("GET:/api/admin/reports/cryptobox-elite-sales") ||
    can("GET:/api/admin/reports/vpn-elite-sales") ||
    can("GET:/api/admin/reports/license-history") ||
    can("GET:/api/admin/reports/service-history") ||
    can("GET:/api/admin/reports/cryptobox-history") ||
    can("GET:/api/admin/reports/income-history") ||
    can("GET:/api/admin/reports/user-sales-report") ||
    can("GET:/api/admin/reports/sales-report") ||
    can("GET:/api/admin/reports/elite-income");

  // 2FA is a personal security setting (token-gated, not role-permission gated),
  // so every authenticated admin can manage their own — Profile group always shows.
  const hasProfile = true;

  // Show a parent group if ANY child the role can access is present,
  // instead of gating the whole group behind one broad "list" permission.
  const hasUsers =
    can("GET:/api/admin/user/list") ||
    can("GET:/api/admin/elites/list") ||
    can("GET:/api/admin/user/bd-requests/review");
  // Deciding who may earn Forex IB income is separable from paying out income
  // that was held back — a role can be granted one without the other.
  const hasForexIb =
    can("GET:/api/admin/ib/requests") || can("GET:/api/admin/ib/banked");
  // Transfers read the admin transfer routes, each checked against the role.
  const hasForexTransfers = [
    "summary",
    "forex-to-mt5",
    "mt5-to-forex",
    "forex-to-gusd",
    "gusd-to-forex",
  ].some((r) => can(`GET:/api/admin/transfers/${r}`));
  const hasRoles =
    can("GET:/api/admin/roles/list") || can("POST:/api/admin/roles/create");
  const hasModerators =
    can("GET:/api/admin/moderators/list") || can("POST:/api/admin/moderators/add");
  const hasDeposits =
    can("GET:/api/admin/deposits/:wallet_type/history") ||
    can("GET:/api/admin/sweep/history");
  const hasSignalX =
    can("GET:/api/admin/services/signalx-list") ||
    can("GET:/api/admin/services/signalx-stats");
  const hasIpvpn =
    can("GET:/api/admin/services/ipvpn-list") ||
    can("GET:/api/admin/services/ipvpn-stats");
  const hasCreditStats =
    can("GET:/api/admin/dashboard/app-credit-stats") ||
    can("GET:/api/admin/dashboard/app-credit-usage") ||
    can("GET:/api/admin/user/:username/app-credit-stats");

  // Section-level visibility: hide a section header entirely when the role
  // has no item under it (same pattern already used for Reports).
  const hasManagement = hasUsers || hasRoles || hasModerators || hasProfile;
  const hasWallets =
    can("GET:/api/admin/wallets/:wallet_type") ||
    can("GET:/api/admin/wallets/adjustments");
  const hasFinance =
    hasWallets ||
    hasDeposits ||
    can("GET:/api/admin/withdrawls/:wallet_type/history");
  const tl = (path) => can(`GET:/api/admin/trust-ledger${path}`);
  const hasLedgerSetup = ["/years", "/currencies", "/cost-centers", "/headings", "/ledgers", "/accounts"].some(tl);
  const hasTrustLedger = tl("/receipts") || tl("/reports/account") || tl("/reports/statement") || hasLedgerSetup;
  const hasCommunity =
    can("POST:/api/admin/user/support-login") ||
    can("POST:/api/admin/general/announcement") ||
    can("GET:/api/admin/faqs") ||
    can("GET:/api/admin/blogs") ||
    hasCreditStats;
  const hasSystem =
    can("GET:/api/admin/general/values-list") ||
    can("GET:/api/admin/general/values/:type");

  return (
    <>
      {/* OVERVIEW */}
      <SidebarGroup className="py-1">
        <SidebarGroupLabel className="px-3 text-[9px] font-semibold uppercase tracking-[0.2em] text-muted-foreground/60">Overview</SidebarGroupLabel>
        <SidebarGroupContent className="space-y-0.5">
          <NavItem to="/" icon={LayoutDashboard} exact>
            Dashboard
          </NavItem>
        </SidebarGroupContent>
      </SidebarGroup>

      {/* MANAGEMENT */}
      {hasManagement && (
      <SidebarGroup className="py-1">
        <SidebarGroupLabel className="px-3 text-[9px] font-semibold uppercase tracking-[0.2em] text-muted-foreground/60">Management</SidebarGroupLabel>
        <SidebarGroupContent className="space-y-0.5">
          {hasUsers && (
            <NavGroup icon={Users} label="Users" matchPaths={["/users", "/elites"]}>
              {can("GET:/api/admin/user/list") && (
                <SubItem to="/users">All Users</SubItem>
              )}
              {can("GET:/api/admin/elites/list") && (
                <SubItem to="/elites">Elite Users</SubItem>
              )}
              {can("GET:/api/admin/user/bd-requests/review") && (
                <SubItem to="/users/bd-verification">BD Verification</SubItem>
              )}
            </NavGroup>
          )}
          {hasRoles && (
            <NavGroup icon={Shield} label="Roles" matchPaths={["/roles"]}>
              {can("GET:/api/admin/roles/list") && (
                <SubItem to="/roles" exact>All Roles</SubItem>
              )}
              {can("POST:/api/admin/roles/create") && (
                <SubItem to="/roles/create">Create Role</SubItem>
              )}
            </NavGroup>
          )}
          {hasModerators && (
            <NavGroup icon={UserCheck} label="Moderators" matchPaths={["/moderators"]}>
              {can("GET:/api/admin/moderators/list") && (
                <SubItem to="/moderators" exact>All Moderators</SubItem>
              )}
              {can("POST:/api/admin/moderators/add") && (
                <SubItem to="/moderators/add">Add Moderator</SubItem>
              )}
            </NavGroup>
          )}
          {hasProfile && (
            <NavGroup icon={User} label="Profile" matchPaths={["/profile"]}>
              {can("POST:/api/admin/account/profile/update-profile") && (
                <SubItem to="/profile">Update Profile</SubItem>
              )}
              {can("POST:/api/admin/account/profile/update-password") && (
                <SubItem to="/profile/password">Change Password</SubItem>
              )}
              <SubItem to="/profile/2fa">Two-Factor Auth</SubItem>
              <SubItem to="/profile/validate-tg">Validate Telegram</SubItem>
            </NavGroup>
          )}
        </SidebarGroupContent>
      </SidebarGroup>
      )}

      {/* KYC — ungated on purpose: every admin reviews identity documents. */}
      <SidebarGroup className="py-1">
        <SidebarGroupLabel className="px-3 text-[9px] font-semibold uppercase tracking-[0.2em] text-muted-foreground/60">KYC</SidebarGroupLabel>
        <SidebarGroupContent className="space-y-0.5">
          <NavItem to="/kyc/identity" icon={ShieldCheck}>
            Identity
          </NavItem>
        </SidebarGroupContent>
      </SidebarGroup>

      {/* FOREX — transfers, gated on the admin transfer routes' verbs. */}
      {hasForexTransfers && (
        <SidebarGroup className="py-1">
          <SidebarGroupLabel className="px-3 text-[9px] font-semibold uppercase tracking-[0.2em] text-muted-foreground/60">Forex</SidebarGroupLabel>
          <SidebarGroupContent className="space-y-0.5">
            <NavItem to="/forex/transfers" icon={ArrowLeftRight}>
              Transfers
            </NavItem>
          </SidebarGroupContent>
        </SidebarGroup>
      )}

      {/* FOREX IB — each item on its own GET; the two verbs are separable. */}
      {hasForexIb && (
        <SidebarGroup className="py-1">
          <SidebarGroupLabel className="px-3 text-[9px] font-semibold uppercase tracking-[0.2em] text-muted-foreground/60">Forex IB</SidebarGroupLabel>
          <SidebarGroupContent className="space-y-0.5">
            {can("GET:/api/admin/ib/requests") && (
              <NavItem to="/forex-ib/requests" icon={UserCheck}>
                IB Requests
              </NavItem>
            )}
            {can("GET:/api/admin/ib/banked") && (
              <NavItem to="/forex-ib/held-income" icon={Coins}>
                Held Income
              </NavItem>
            )}
          </SidebarGroupContent>
        </SidebarGroup>
      )}

      {/* FINANCE */}
      {hasFinance && (
      <SidebarGroup className="py-1">
        <SidebarGroupLabel className="px-3 text-[9px] font-semibold uppercase tracking-[0.2em] text-muted-foreground/60">Finance</SidebarGroupLabel>
        <SidebarGroupContent className="space-y-0.5">
          {hasWallets && (
            <NavGroup icon={Wallet} label="Wallets" matchPaths={["/wallets"]}>
              {can("GET:/api/admin/wallets/:wallet_type") && (
                <>
                  <SubItem to="/wallets/gusd">GUSD Wallets</SubItem>
                  <SubItem to="/wallets/bfm">BFM Wallets</SubItem>
                  <SubItem to="/wallets/app-credit">Credits</SubItem>
                  <SubItem to="/wallets/forex">FOREX Wallets</SubItem>
                </>
              )}
              {can("GET:/api/admin/wallets/adjustments") && (
                <SubItem to="/wallets/adjustments">Balance Adjustments</SubItem>
              )}
            </NavGroup>
          )}
          {hasDeposits && (
            <NavGroup icon={ArrowDownToLine} label="Deposits" matchPaths={["/deposits"]}>
              {can("GET:/api/admin/deposits/:wallet_type/history") && (
                <>
                  <SubItem to="/deposits/list">List</SubItem>
                  <SubItem to="/deposits/credits-per-user">Credit Deposit User</SubItem>
                  <SubItem to="/deposits/credits-history">Credit Deposit Record</SubItem>
                </>
              )}
              {can("GET:/api/admin/sweep/history") && (
                <SubItem to="/deposits/gusd-sweep">GUSD Deposit Sweep</SubItem>
              )}
            </NavGroup>
          )}
          {can("GET:/api/admin/withdrawls/:wallet_type/history") && (
            <NavGroup icon={ArrowUpFromLine} label="Withdrawals" matchPaths={["/withdrawals"]}>
              <SubItem to="/withdrawals/gusd">GUSD Withdrawals</SubItem>
              <SubItem to="/withdrawals/bfm">BFM Withdrawals</SubItem>
            </NavGroup>
          )}
        </SidebarGroupContent>
      </SidebarGroup>
      )}

      {/* TRUST LEDGER */}
      {hasTrustLedger && (
        <SidebarGroup className="py-1">
          <SidebarGroupLabel className="px-3 text-[9px] font-semibold uppercase tracking-[0.2em] text-muted-foreground/60">Trust Ledger</SidebarGroupLabel>
          <SidebarGroupContent className="space-y-0.5">
            {tl("/receipts") && (
              <NavItem to="/trust-ledger/receipts" icon={FileText}>
                Vouchers
              </NavItem>
            )}
            {tl("/reports/account") && (
              <NavItem to="/trust-ledger/statement" icon={ScrollText}>
                Account Statement
              </NavItem>
            )}
            {tl("/reports/statement") && (
              <NavItem to="/trust-ledger/financial-statements" icon={Landmark}>
                Financial Statements
              </NavItem>
            )}
            {hasLedgerSetup && (
              <NavItem to="/trust-ledger/setup" icon={SlidersHorizontal}>
                Setup
              </NavItem>
            )}
          </SidebarGroupContent>
        </SidebarGroup>
      )}

      {/* REPORTS */}
      {hasReports && (
        <SidebarGroup className="py-1">
          <SidebarGroupLabel className="px-3 text-[9px] font-semibold uppercase tracking-[0.2em] text-muted-foreground/60">Reports</SidebarGroupLabel>
          <SidebarGroupContent className="space-y-0.5">
            <NavGroup icon={BarChart2} label="Reports" matchPaths={["/reports"]}>
              {can("GET:/api/admin/reports/income/:income_type") && <SubItem to="/reports/sponsor-bonus">Sponsor Bonus</SubItem>}
              {can("GET:/api/admin/reports/sponsor-bonus-report") && <SubItem to="/reports/sponsor-bonus-level-wise">Sponsor Bonus Level Wise</SubItem>}
              {can("GET:/api/admin/reports/statement/:wallet_type") && <SubItem to="/reports/wallet-statement">Wallet Statement</SubItem>}
              {can("GET:/api/admin/reports/buy-history") && <SubItem to="/reports/buy-history">Buy History</SubItem>}
              {can("GET:/api/admin/reports/bfm-buy-history") && <SubItem to="/reports/buy-bfm">Buy BFM</SubItem>}
              {can("GET:/api/admin/reports/cryptobox-elite-sales") && <SubItem to="/reports/cryptobox-elite-sales">CryptoBox Elite Sales</SubItem>}
              {can("GET:/api/admin/reports/vpn-elite-sales") && <SubItem to="/reports/vpn-elite-sales">VPN Elite Sales</SubItem>}
              {can("GET:/api/admin/reports/license-history") && <SubItem to="/reports/license-history">License History</SubItem>}
              {can("GET:/api/admin/reports/service-history") && <SubItem to="/reports/service-history">Service History</SubItem>}
              {can("GET:/api/admin/reports/cryptobox-history") && <SubItem to="/reports/cryptobox-history">CryptoBox History</SubItem>}
              {can("GET:/api/admin/reports/income-history") && <SubItem to="/reports/income-history">General Income History</SubItem>}
              {can("GET:/api/admin/reports/user-sales-report") && <SubItem to="/reports/user-sales">User Sales Report</SubItem>}
              {can("GET:/api/admin/reports/sales-report") && <SubItem to="/reports/sales-report">Sales Report</SubItem>}
              {can("GET:/api/admin/reports/elite-income") && <SubItem to="/reports/elite-income">Elite Income Report</SubItem>}
            </NavGroup>
          </SidebarGroupContent>
        </SidebarGroup>
      )}

      {/* SERVICES */}
      <SidebarGroup className="py-1">
        <SidebarGroupLabel className="px-3 text-[9px] font-semibold uppercase tracking-[0.2em] text-muted-foreground/60">Services</SidebarGroupLabel>
        <SidebarGroupContent className="space-y-0.5">
          {hasSignalX && (
            <NavGroup icon={Radio} label="Signal X" matchPaths={["/services/signalx"]}>
              {can("GET:/api/admin/services/signalx-list") && (
                <SubItem to="/services/signalx/list">List</SubItem>
              )}
              {can("GET:/api/admin/services/signalx-stats") && (
                <SubItem to="/services/signalx/stats">Stats</SubItem>
              )}
            </NavGroup>
          )}
          {hasIpvpn && (
            <NavGroup icon={Globe} label="IPVPN" matchPaths={["/services/ipvpn"]}>
              {can("GET:/api/admin/services/ipvpn-list") && (
                <SubItem to="/services/ipvpn/list">List</SubItem>
              )}
              {can("GET:/api/admin/services/ipvpn-stats") && (
                <SubItem to="/services/ipvpn/stats">Stats</SubItem>
              )}
            </NavGroup>
          )}
          {(can("GET:/api/admin/lottery/box-lottery") || can("GET:/api/admin/lottery/daily-lottery")) && (
            <NavGroup icon={Ticket} label="Lottery" matchPaths={["/lottery"]}>
              {can("GET:/api/admin/lottery/box-lottery") && <SubItem to="/lottery/box">Box Lottery</SubItem>}
              {can("GET:/api/admin/lottery/daily-lottery") && <SubItem to="/lottery/daily">Daily Lottery</SubItem>}
            </NavGroup>
          )}
          {/* Ginox AI & Gidex Fees use separate external admin APIs
              (REACT_APP_GINOX_AI_API_URL / REACT_APP_GIDEX_API_URL with a
              static admin key) and have no role-permission, so they are not
              gated by can(). */}
          <NavGroup icon={Bot} label="Ginox AI" matchPaths={["/ginox-ai"]}>
            <SubItem to="/ginox-ai/status">AI Status</SubItem>
            <SubItem to="/ginox-ai/payments">AI Payments</SubItem>
          </NavGroup>
          <NavGroup icon={Layers} label="Gidex Fees" matchPaths={["/gidex-fees"]}>
            <SubItem to="/gidex-fees">Manage Fees</SubItem>
          </NavGroup>
        </SidebarGroupContent>
      </SidebarGroup>

      {/* COMMUNITY */}
      {hasCommunity && (
      <SidebarGroup className="py-1">
        <SidebarGroupLabel className="px-3 text-[9px] font-semibold uppercase tracking-[0.2em] text-muted-foreground/60">Community</SidebarGroupLabel>
        <SidebarGroupContent className="space-y-0.5">
          {can("POST:/api/admin/user/support-login") && (
            <NavGroup icon={Headphones} label="Support" matchPaths={["/support"]}>
              <SubItem to="/support/SupportLogin">Support Login</SubItem>
            </NavGroup>
          )}
          {can("POST:/api/admin/general/announcement") && (
            <NavGroup icon={Bell} label="Announcement" matchPaths={["/announcement"]}>
              <SubItem to="/announcement/AnnouncementPage">Announcement</SubItem>
            </NavGroup>
          )}
          {can("GET:/api/admin/faqs") && (
            <NavGroup icon={CircleHelp} label="FAQs" matchPaths={["/faqs"]}>
              <SubItem to="/faqs">All FAQs</SubItem>
            </NavGroup>
          )}
          {can("GET:/api/admin/blogs") && (
            <NavGroup icon={Newspaper} label="Blog" matchPaths={["/blogs"]}>
              <SubItem to="/blogs" exact>All posts</SubItem>
              {can("POST:/api/admin/blogs") && <SubItem to="/blogs/new">New post</SubItem>}
            </NavGroup>
          )}
          {hasCreditStats && (
            <NavGroup icon={Coins} label="Credit Stats" matchPaths={["/credit-stats"]}>
              {can("GET:/api/admin/dashboard/app-credit-stats") && (
                <SubItem to="/credit-stats/app">Credit Stats</SubItem>
              )}
              {can("GET:/api/admin/user/:username/app-credit-stats") && (
                <SubItem to="/credit-stats/user">User Credit Stats</SubItem>
              )}
              {can("GET:/api/admin/dashboard/app-credit-usage") && (
                <SubItem to="/credit-stats/usage">Credits Usage History</SubItem>
              )}
            </NavGroup>
          )}
        </SidebarGroupContent>
      </SidebarGroup>
      )}

      {/* SYSTEM */}
      {hasSystem && (
      <SidebarGroup className="py-1">
        <SidebarGroupLabel className="px-3 text-[9px] font-semibold uppercase tracking-[0.2em] text-muted-foreground/60">System</SidebarGroupLabel>
        <SidebarGroupContent className="space-y-0.5">
          {can("GET:/api/admin/general/values-list") && (
            <NavGroup icon={Gem} label="Points" matchPaths={["/values"]}>
              <SubItem to="/values">Points</SubItem>
            </NavGroup>
          )}
          {can("GET:/api/admin/general/values/:type") && (
            <NavGroup icon={Settings} label="Settings" matchPaths={["/settings"]}>
              <SubItem to="/settings/rebate">Rebate Settings</SubItem>
            </NavGroup>
          )}
        </SidebarGroupContent>
      </SidebarGroup>
      )}
    </>
  );
}

/* ── Admin Sidebar (uses Radix UI for Hyperliquid behavior) */

export function AdminSidebar() {
  return (
    <Sidebar
      collapsible="offcanvas"
      className="border-r border-sidebar-border"
      style={{ "--sidebar-background": "10 12 16" }}
    >
      <SidebarHeader className="h-16 flex-row items-center gap-3 px-5 border-b border-sidebar-border bg-[#0a0c10]">
        <img
          src={process.env.PUBLIC_URL + "/logo.png"}
          alt="Ginox"
          className="h-8 w-8 shrink-0 object-contain"
        />
        <span className="font-serif text-[20px] italic leading-none text-sidebar-foreground">
          Ginox
        </span>
      </SidebarHeader>
      <SidebarContent className="bg-[#0a0c10] px-2 pt-3 pb-6 overflow-y-auto">
        <NavContent />
      </SidebarContent>
    </Sidebar>
  );
}

export default AdminSidebar;
